import { useState } from 'react';
import { api, query } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { ChangeRequest } from '../lib/types';
import { Badge, Card, Empty, Modal, Notice, PageHead, Tabs } from '../components/ui';
import { ChangeRequestDiff } from '../components/ChangeRequestDiff';

type Filter = 'pending' | 'approved' | 'rejected';

/**
 * The queue. There is no notification store behind it — the open requests
 * are the notification, and this page and the badge read the same rows.
 */
export function Approvals() {
  const [filter, setFilter] = useState<Filter>('pending');
  const requests = useAsync(
    () => api.get<ChangeRequest[]>(`/api/admin/approvals${query({ state: filter })}`),
    [filter],
  );
  const [open, setOpen] = useState<string | null>(null);

  return (
    <>
      <PageHead
        title="Approvals"
        description="What people have asked for. An application keeps running on its approved configuration until a request is decided."
      />

      <Tabs
        active={filter}
        onChange={setFilter}
        tabs={[
          { id: 'pending', label: 'Open' },
          { id: 'approved', label: 'Approved' },
          { id: 'rejected', label: 'Rejected' },
        ]}
      />

      {requests.error ? <Notice tone="danger">{requests.error}</Notice> : null}

      <Card tight>
        {requests.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Application</th>
                <th>Requested by</th>
                <th>Kind</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {requests.data.map((request) => (
                <tr key={request.id}>
                  <td>
                    <strong>{request.application_name}</strong>
                    {request.note ? <div className="muted tiny">{request.note}</div> : null}
                  </td>
                  <td className="muted">{request.requested_by_label}</td>
                  <td>
                    <Badge tone={request.kind === 'delete' ? 'danger' : 'accent'}>
                      {request.kind}
                    </Badge>
                  </td>
                  <td className="muted tiny">{new Date(request.requested_at).toLocaleString()}</td>
                  <td className="num">
                    <button className="small" onClick={() => setOpen(request.id)}>
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>Nothing waiting.</Empty>
        )}
      </Card>

      {open ? (
        <ReviewDialog
          requestId={open}
          onClose={() => setOpen(null)}
          onDecided={() => {
            setOpen(null);
            requests.reload();
          }}
        />
      ) : null}
    </>
  );
}

function ReviewDialog({
  requestId,
  onClose,
  onDecided,
}: {
  requestId: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  const request = useAsync(
    () => api.get<ChangeRequest>(`/api/admin/approvals/${requestId}`),
    [requestId],
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function decide(decision: 'approve' | 'reject'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/api/admin/approvals/${requestId}/${decision}`,
        decision === 'reject' ? { note } : {},
      );
      onDecided();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const data = request.data;
  const decided = data ? data.state !== 'pending' : false;

  return (
    <Modal
      title={data ? `${data.application_name} — ${data.kind}` : 'Request'}
      onClose={onClose}
      footer={
        decided ? (
          <button onClick={onClose}>Close</button>
        ) : (
          <>
            <button onClick={onClose}>Cancel</button>
            <button onClick={() => void decide('reject')} disabled={busy || note.trim().length < 3}>
              Reject
            </button>
            <button className="primary" onClick={() => void decide('approve')} disabled={busy}>
              Approve
            </button>
          </>
        )
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {request.error ? <Notice tone="danger">{request.error}</Notice> : null}
      {data ? (
        <>
          <p className="muted">
            Requested by {data.requested_by_label} on{' '}
            {new Date(data.requested_at).toLocaleString()}
          </p>
          {data.note ? <Notice tone="info">{data.note}</Notice> : null}
          {data.staleAgainst ? (
            <Notice tone="warn" title="The application has changed since this was submitted">
              The diff below is against the state at submission. Approving applies the proposal
              whole, so it will also undo what changed in the meantime.
            </Notice>
          ) : null}
          {data.diff ? <ChangeRequestDiff diff={data.diff} /> : null}
          {decided ? (
            <Notice tone={data.state === 'approved' ? 'ok' : 'warn'}>
              {data.state} by {data.reviewed_by}
              {data.review_note ? ` — ${data.review_note}` : ''}
            </Notice>
          ) : (
            <label className="field">
              <span>
                Reason <span className="hint">— required to reject, ignored when approving</span>
              </span>
              <textarea value={note} onChange={(event) => setNote(event.target.value)} />
            </label>
          )}
        </>
      ) : null}
    </Modal>
  );
}
